import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Textarea,
} from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";

import FileDropZone from "@/components/common/FileDropZone";
import { listPartners } from "@/features/partners/api";
import { listUsers, userLabel } from "@/features/users/api";
import { errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import {
  createProject,
  importProjectDrawings,
  importProjectOrder,
  importPurchaseOrder,
  importShintoProjects,
  type Project,
  type ProjectImportResult,
  type ProjectInput,
  type ShintoProjectImportResult,
} from "./api";

/**
 * 発注 Excel の下見。**登録を押す前に、何が起こされるかを出す**。
 *
 * ここで見せる内容は実行と同じ解析から来るので、見えたとおりの案件ができる。
 * セット品（同じ内容の購番が複数本）は**紙の行数と明細の行数が違う**ので、
 * その差がひと目で分かるように並べる。
 */
function OrderPreview({ preview }: { preview: ProjectImportResult }) {
  if (!preview.can_apply) {
    return (
      <div className="space-y-1 rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700">
        <p className="flex items-center gap-1.5 font-bold">
          <AlertTriangle size={16} />
          このファイルは取り込めません
        </p>
        <ul className="list-disc space-y-0.5 pl-5">
          {preview.blockers.map((blocker) => (
            <li key={blocker}>{blocker}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-lg bg-default-50 px-4 py-3 text-sm">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-bold">{preview.client_name}</span>
        <span className="text-default-600">{preview.project_title}</span>
        {preview.is_set_product && (
          <Chip size="sm" variant="flat" color="primary">
            セット品 {preview.set_count} セット
          </Chip>
        )}
      </p>
      <p className="text-default-600">
        客先購番 {preview.client_order_numbers.join(" / ")}
      </p>
      <p className="text-default-600">
        {preview.is_set_product
          ? `紙の ${preview.paper_row_count} 行を ${preview.item_count} 行に畳みます（数量はセットぶん合算）。`
          : `明細 ${preview.item_count} 行を起こします。`}
      </p>
      <div className="max-h-40 overflow-auto rounded-lg border border-default-200 bg-content1">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-default-50 text-left text-tiny text-default-600">
            <tr className="h-8">
              <th className="px-3 font-bold">図番</th>
              <th className="px-3 font-bold">品名</th>
              <th className="px-3 text-right font-bold">数量</th>
            </tr>
          </thead>
          <tbody>
            {preview.items.map((item) => (
              <tr key={`${item.client_bom_no}-${item.drawing_number}`} className="h-8">
                <td className="px-3">{item.drawing_number}</td>
                <td className="px-3">{item.item_name}</td>
                <td className="px-3 text-right tabular-nums">{item.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preview.warnings.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-tiny text-warning-600">
          {preview.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * 発注ファイル（新東工業）の下見。**1 ファイルで複数の案件ができる**ので、何件
 * 起きるのかと、製番ごとの中身を出す。
 *
 * 既に取り込んだ製番は起こさない（その案件の工番を添えて灰色で出す）。客先は
 * 追加分を足して送り直してくるので、同じ製番が 2 度来るのは普通に起きる。
 */
function ShintoPreview({ preview }: { preview: ShintoProjectImportResult }) {
  if (!preview.can_apply) {
    return (
      <div className="space-y-1 rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700">
        <p className="flex items-center gap-1.5 font-bold">
          <AlertTriangle size={16} />
          このファイルは取り込めません
        </p>
        <ul className="list-disc space-y-0.5 pl-5">
          {preview.blockers.map((blocker) => (
            <li key={blocker}>{blocker}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-lg bg-default-50 px-4 py-3 text-sm">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-bold">{preview.client_name}</span>
        <Chip size="sm" variant="flat" color="primary">
          {preview.project_count} 件の案件
        </Chip>
        <span className="text-default-600">
          紙の {preview.row_count} 行を製番ごとに分けます
        </span>
      </p>
      <div className="max-h-56 space-y-2 overflow-auto">
        {preview.projects.map((project) => (
          <div
            key={project.client_po_number}
            className={`rounded-lg border px-3 py-2 ${
              project.existing_project_number
                ? "border-default-200 bg-default-100 text-default-500"
                : "border-default-200 bg-content1"
            }`}
          >
            <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="font-bold">{project.client_po_number}</span>
              <span className="text-tiny text-default-500">
                明細 {project.item_count} 行
                {/* 納期が複数ある案件は案件側が空になる。行ごとに入ることを
                    言わないと、納期が落ちたように見える。 */}
                {project.deadline ? ` / 納期 ${project.deadline}` : " / 納期は明細ごと"}
              </span>
              {project.existing_project_number && (
                <Chip size="sm" variant="flat">
                  既にあります（{project.existing_project_number}）
                </Chip>
              )}
            </p>
            {!project.existing_project_number && (
              <p className="mt-0.5 truncate text-tiny text-default-500">
                {project.items.map((item) => item.drawing_number).join(" / ")}
              </p>
            )}
          </div>
        ))}
      </div>
      <p className="text-tiny text-default-500">
        案件名・客先注文番号・受注日・納期は紙の値（製番ごとに違うため）。
        直送先・営業担当・備考は、ここで入れた値を全件に入れます。
      </p>
      {preview.warnings.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-tiny text-warning-600">
          {preview.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** select で「未設定」を表す内部キー（空文字は HeroUI が扱えないため）。 */
const NONE_KEY = "__none__";

/** 今日（ローカル時刻）を YYYY-MM-DD で返す。 */
function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * 案件の新規登録ダイアログ。
 *
 * ここから起こすのは**特急案件**（見積より先に製作が始まるもの）が主。見積が
 * 先にある通常フローでは、見積詳細の「この見積を受注にする」を使うと明細ごと
 * コピーされる。
 *
 * 工番は登録時に自動採番される。必須の入力は無く（案件を識別するのは工番）、
 * 残りは案件管理画面で埋める。
 *
 * **発注書と図面をここで一緒に上げられる。** どちらも読み取りはワーカーがやる
 * ので、登録したらすぐ閉じて案件詳細へ移る。明細と図面は数秒後に出てくる
 * （[[purchase_orders]] / [[drawings]]）。
 *
 * **人が入れた値が紙より優先される。** 日付欄は既定で今日が入っているが、
 * 発注書を一緒に上げるときに**触っていない日付は空で送る**。そうしないと既定値
 * が「人の入力」として扱われ、紙の発注日が永久に入らない。触ったかどうかは
 * `touched` で見ている（値を既定と比べるのでは、今日を選び直した人と区別が
 * つかない）。
 *
 * **客先ごとの一括取り込みを 2 つ持っている。** 京セラは発注 Excel から 1 案件、
 * 新東工業は案件登録用のファイル（CSV / .xls / .xlsx）から**製番ごとに複数案件**。
 * どちらも選んだ時点で下見するので、登録を押してから様式違いで失敗することがない。
 *
 * `onCreated` が配列なのは新東工業の CSV が複数案件を起こすため（他の経路は
 * 常に 1 件）。
 */
export default function ProjectCreateDialog({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (projects: Project[]) => void;
}) {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [clientId, setClientId] = useState("");
  const [shipToId, setShipToId] = useState("");
  const [salesRepId, setSalesRepId] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [orderDate, setOrderDate] = useState(today());
  const [deadline, setDeadline] = useState("");
  const [shippingPlannedDate, setShippingPlannedDate] = useState("");
  const [note, setNote] = useState("");
  const [purchaseOrderFile, setPurchaseOrderFile] = useState<File | null>(null);
  const [drawingFiles, setDrawingFiles] = useState<File[]>([]);
  /** 京セラの発注 Excel と、その下見の結果（選んだ時点で読んで見せる）。 */
  const [orderFile, setOrderFile] = useState<File | null>(null);
  const [orderPreview, setOrderPreview] = useState<ProjectImportResult | null>(null);
  /** 新東工業の案件登録用ファイルと、その下見の結果（選んだ時点で読んで見せる）。 */
  const [shintoFile, setShintoFile] = useState<File | null>(null);
  const [shintoPreview, setShintoPreview] = useState<ShintoProjectImportResult | null>(
    null,
  );
  /**
   * 新東工業のファイルを選んだときに、発注書・図面の選択を外したか。
   *
   * **外したことを言うために要る。** この経路は案件を複数起こすので、どの案件に
   * 付けるか決められず発注書・図面を送れない。黙って捨てると「上げたつもり」で
   * 画面が閉じる（起票は成功するので失敗にも見えない）。
   */
  const [droppedAttachments, setDroppedAttachments] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  // 人が実際に触った日付欄。既定値（今日）を「人の入力」と見なさないために要る。
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // 登録後、ファイルを送っている間に出す文言。
  const [progress, setProgress] = useState<string | null>(null);

  const markTouched = (field: string) =>
    setTouched((prev) => (prev.has(field) ? prev : new Set(prev).add(field)));

  useEffect(() => {
    if (!isOpen) return;
    setTitle("");
    setClientId("");
    setShipToId("");
    setSalesRepId(user?.user_id ?? "");
    setPoNumber("");
    setOrderDate(today());
    setDeadline("");
    setShippingPlannedDate("");
    setNote("");
    setPurchaseOrderFile(null);
    setDrawingFiles([]);
    setOrderFile(null);
    setOrderPreview(null);
    setShintoFile(null);
    setShintoPreview(null);
    setDroppedAttachments(false);
    setOrderError(null);
    setChecking(false);
    setTouched(new Set());
    setError(null);
    setSaving(false);
    setProgress(null);
  }, [isOpen, user]);

  // 得意先は取引中のものだけを候補に出す。
  const partners = useQuery({
    queryKey: ["partners", "picker"],
    queryFn: () =>
      listPartners({ disabled: false, isClient: true, limit: 500, offset: 0 }),
    enabled: isOpen,
  });
  // 直送先は用途が「直送先」の取引先だけ。得意先と同じマスタだが、候補に
  // 混ぜると送り先を取り違える。
  const shipTos = useQuery({
    queryKey: ["partners", "shipTo", "picker"],
    queryFn: () =>
      listPartners({ disabled: false, isShipTo: true, limit: 500, offset: 0 }),
    enabled: isOpen,
  });
  const users = useQuery({
    queryKey: ["users", "picker"],
    queryFn: () => listUsers(),
    enabled: isOpen,
  });

  /**
   * 選ばれた発注 Excel を**その場で下見する**（`dry_run`）。
   *
   * 様式が合わない紙はここで分かるので、登録を押してから失敗するのを避けられる。
   * 下見と実行は同じ解析を通るので、ここで見えた内容がそのまま起票される。
   */
  const checkOrderFile = async (file: File | null) => {
    setOrderFile(file);
    setOrderPreview(null);
    setOrderError(null);
    setError(null);
    if (file == null) return;

    setChecking(true);
    try {
      setOrderPreview(await importProjectOrder({ file, dryRun: true }));
    } catch (e) {
      setOrderError(errorMessage(e, "このファイルは読み取れませんでした。"));
    } finally {
      setChecking(false);
    }
  };

  /**
   * 選ばれた案件登録用ファイル（新東工業）を**その場で下見する**（`dry_run`）。
   *
   * 何件の案件が起きるのかはここでしか分からない（製番で割るので紙を見ても
   * 数えにくい）。既に取り込んだ製番もここで分かる。
   */
  const checkShintoFile = async (file: File | null) => {
    setShintoFile(file);
    setShintoPreview(null);
    setOrderError(null);
    setError(null);
    if (file == null) {
      setDroppedAttachments(false);
      return;
    }

    // **先に選ばれていた発注書・図面はここで外す。** この経路では送れないので、
    // 持ったままにすると送ったつもりで消える。外したことは下の文で伝える。
    setDroppedAttachments(purchaseOrderFile != null || drawingFiles.length > 0);
    setPurchaseOrderFile(null);
    setDrawingFiles([]);

    setChecking(true);
    try {
      setShintoPreview(await importShintoProjects({ file, dryRun: true }));
    } catch (e) {
      setOrderError(errorMessage(e, "このファイルは読み取れませんでした。"));
    } finally {
      setChecking(false);
    }
  };

  const submit = async () => {
    // **発注書を一緒に上げるときは、触っていない日付を空で送る。** 受注日には
    // 既定で今日が入っているので、そのまま送ると「人が入れた値」として扱われ、
    // 紙の発注日が入らなくなる（サーバーは空いている欄だけを紙で埋める）。
    const fromPaper = purchaseOrderFile != null;
    const dateOrNull = (field: string, value: string) =>
      fromPaper && !touched.has(field) ? null : value || null;

    const input: ProjectInput = {
      project_title: title.trim() || null,
      client_id: clientId ? Number(clientId) : null,
      ship_to_id: shipToId ? Number(shipToId) : null,
      sales_rep_id: salesRepId || null,
      client_po_number: poNumber.trim() || null,
      order_date: dateOrNull("order_date", orderDate),
      deadline: dateOrNull("deadline", deadline),
      shipping_planned_date: dateOrNull("shipping_planned_date", shippingPlannedDate),
      note: note.trim() || null,
    };

    setSaving(true);
    setError(null);
    try {
      // **新東工業のファイルは製番ごとに複数の案件を起こす。** 案件ごとに違う値は
      // CSV の値を使うので、案件をまたいで同じに使える項目だけを渡す。発注書と
      // 図面は案件を 1 つに決められないのでこの経路では受け付けていない。
      if (shintoFile != null) {
        const result = await importShintoProjects({
          file: shintoFile,
          dryRun: false,
          shared: {
            ship_to_id: input.ship_to_id,
            sales_rep_id: input.sales_rep_id,
            note: input.note,
          },
        });
        onCreated(result.created);
        onClose();
        return;
      }

      // **Excel があればそれで起こす。** 案件・明細・客先購番の行が 1 度に
      // できる（人が入れた値は紙より優先なので、埋めた項目だけ渡す）。
      const project =
        orderFile == null
          ? await createProject(input)
          : ((await importProjectOrder({ file: orderFile, dryRun: false, input }))
              .project as Project);

      // **案件はもう出来ている。** ここから先で失敗しても案件は消さず、どの
      // ファイルが入らなかったかだけ伝える（作り直させるほうが手間になる）。
      const failures: string[] = [];
      if (purchaseOrderFile) {
        setProgress("発注書を取り込んでいます…");
        try {
          await importPurchaseOrder(project.project_id, purchaseOrderFile);
        } catch (e) {
          failures.push(errorMessage(e, "発注書を取り込めませんでした。"));
        }
      }
      for (const file of drawingFiles) {
        setProgress(`図面を取り込んでいます…（${file.name}）`);
        try {
          await importProjectDrawings(project.project_id, file);
        } catch (e) {
          failures.push(errorMessage(e, `${file.name} を取り込めませんでした。`));
        }
      }

      if (failures.length > 0) {
        // 案件は出来たので閉じない。入らなかったものを見せて、案件詳細から
        // やり直してもらう。
        setError(
          `案件は登録しました（${project.project_number}）が、${failures.join(" / ")}`,
        );
        onCreated([project]);
        return;
      }

      onCreated([project]);
      onClose();
    } catch (e) {
      setError(errorMessage(e, "案件を起票できませんでした。"));
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>案件の新規登録</span>
          <span className="text-small font-normal text-default-500">
            工番は登録時に自動で採番されます。見積が先にある場合は、見積管理画面の「この見積を受注にする」を使うと明細ごとコピーされます。
          </span>
        </ModalHeader>

        <ModalBody>
          <Input
            label="案件名"
            labelPlacement="outside"
            placeholder=" "
            aria-label="案件名"
            variant="bordered"
            radius="sm"
            description="受注時点で決まっていなければ空のままで構いません。"
            value={title}
            onValueChange={(v) => {
              setTitle(v);
              setError(null);
            }}
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Select
              label="得意先"
              labelPlacement="outside"
              placeholder="（未設定）"
              aria-label="得意先"
              variant="bordered"
              radius="sm"
              isLoading={partners.isPending}
              disallowEmptySelection
              selectedKeys={[clientId || NONE_KEY]}
              onSelectionChange={(keys) => {
                const key = [...keys][0] as string | undefined;
                setClientId(!key || key === NONE_KEY ? "" : key);
              }}
            >
              {[
                { key: NONE_KEY, label: "（未設定）" },
                ...(partners.data?.items ?? []).map((p) => ({
                  key: String(p.partner_id),
                  label: `${p.partner_code} ${p.partner_name}`,
                })),
              ].map((option) => (
                <SelectItem key={option.key}>{option.label}</SelectItem>
              ))}
            </Select>

            <Select
              label="直送先"
              labelPlacement="outside"
              placeholder="（直送しない）"
              aria-label="直送先"
              variant="bordered"
              radius="sm"
              isLoading={shipTos.isPending}
              disallowEmptySelection
              selectedKeys={[shipToId || NONE_KEY]}
              onSelectionChange={(keys) => {
                const key = [...keys][0] as string | undefined;
                setShipToId(!key || key === NONE_KEY ? "" : key);
              }}
            >
              {[
                { key: NONE_KEY, label: "（直送しない）" },
                ...(shipTos.data?.items ?? []).map((p) => ({
                  key: String(p.partner_id),
                  label: `${p.partner_code} ${p.partner_name}`,
                })),
              ].map((option) => (
                <SelectItem key={option.key}>{option.label}</SelectItem>
              ))}
            </Select>

            <Select
              label="営業担当"
              labelPlacement="outside"
              placeholder="（未設定）"
              aria-label="営業担当"
              variant="bordered"
              radius="sm"
              isLoading={users.isPending}
              disallowEmptySelection
              selectedKeys={[salesRepId || NONE_KEY]}
              onSelectionChange={(keys) => {
                const key = [...keys][0] as string | undefined;
                setSalesRepId(!key || key === NONE_KEY ? "" : key);
              }}
            >
              {[
                { key: NONE_KEY, label: "（未設定）" },
                ...(users.data?.items ?? []).map((u) => ({
                  key: u.user_id,
                  label: userLabel(u),
                })),
              ].map((option) => (
                <SelectItem key={option.key}>{option.label}</SelectItem>
              ))}
            </Select>

            <Input
              label="客先注文番号"
              labelPlacement="outside"
              placeholder=" "
              aria-label="客先注文番号"
              variant="bordered"
              radius="sm"
              value={poNumber}
              onValueChange={setPoNumber}
            />
            <Input
              label="受注日"
              labelPlacement="outside"
              placeholder=" "
              aria-label="受注日"
              type="date"
              variant="bordered"
              radius="sm"
              value={orderDate}
              onValueChange={(v) => {
                setOrderDate(v);
                markTouched("order_date");
              }}
            />
            <Input
              label="納期"
              labelPlacement="outside"
              placeholder=" "
              aria-label="納期"
              type="date"
              variant="bordered"
              radius="sm"
              value={deadline}
              onValueChange={(v) => {
                setDeadline(v);
                markTouched("deadline");
              }}
            />
            <Input
              label="出荷予定日"
              labelPlacement="outside"
              placeholder=" "
              aria-label="出荷予定日"
              type="date"
              variant="bordered"
              radius="sm"
              value={shippingPlannedDate}
              onValueChange={(v) => {
                setShippingPlannedDate(v);
                markTouched("shipping_planned_date");
              }}
            />
          </div>

          <Textarea
            label="備考"
            labelPlacement="outside"
            placeholder=" "
            aria-label="備考"
            variant="bordered"
            radius="sm"
            minRows={2}
            value={note}
            onValueChange={setNote}
          />

          {/* 発注書と図面。どちらも任意で、読み取りは登録後にワーカーが行う。 */}
          <div className="mt-1 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <span className="text-small text-foreground">発注書 PDF</span>
              <div className="mt-1">
                <FileDropZone
                  label="発注書 PDF"
                  accept=".pdf"
                  showLabel={false}
                  hint="図番・数量・単価を読み取って明細にし、図面と結びます"
                  file={purchaseOrderFile}
                  isDisabled={saving || shintoFile != null}
                  onSelect={(f) => {
                    setPurchaseOrderFile(f);
                    setError(null);
                  }}
                />
              </div>
            </div>

            <div>
              <span className="text-small text-foreground">図面 PDF</span>
              <div className="mt-1">
                <FileDropZone
                  label="図面 PDF"
                  accept=".pdf"
                  showLabel={false}
                  multiple
                  hint="図番を読み取り、同じ図番の明細に自動で紐づけます"
                  files={drawingFiles}
                  isDisabled={saving || shintoFile != null}
                  onSelectMany={(files) => {
                    setDrawingFiles(files);
                    setError(null);
                  }}
                />
              </div>
            </div>
          </div>

          {purchaseOrderFile && (
            <p className="text-tiny text-default-500">
              読み取りには数秒かかります。登録するとこの画面は閉じ、明細は案件の画面に出てきます。
            </p>
          )}

          {/* 客先ごとの一括取り込み。**選んだ時点で様式を確かめて中身を見せる**
              ので、登録を押してから様式違いで失敗することがない。

              **どちらか一方だけ選べる。** 片方を選んだらもう片方を閉じる
              （両方の紙から 1 つの案件を起こす形が無い）。 */}
          <div className="mt-1 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <span className="text-small text-foreground">
                エクセルファイル（京セラ専用）
              </span>
              <div className="mt-1">
                <FileDropZone
                  label="発注明細の Excel（京セラ専用）"
                  accept=".xlsx"
                  showLabel={false}
                  hint="発注明細から案件と明細を起こします（セット品は畳みます）"
                  file={orderFile}
                  isDisabled={saving || shintoFile != null}
                  onSelect={(f) => void checkOrderFile(f)}
                />
              </div>
            </div>

            <div>
              <span className="text-small text-foreground">
                CSV / Excel（新東工業専用）
              </span>
              <div className="mt-1">
                <FileDropZone
                  label="案件登録用のファイル（新東工業専用）"
                  /* 客先は同じ表を CSV と .xls の両方で送ってくる。様式を決めるのは
                     ファイルの中身なので、ここは選ばせる拡張子を広げるだけ。 */
                  accept=".csv,.xls,.xlsx"
                  showLabel={false}
                  hint="製番ごとに案件を起こします（1 ファイルで複数案件）"
                  file={shintoFile}
                  isDisabled={saving || orderFile != null}
                  onSelect={(f) => void checkShintoFile(f)}
                />
              </div>
            </div>
          </div>

          {shintoFile != null && (
            <p
              className={`text-tiny ${
                droppedAttachments ? "text-warning-600" : "text-default-500"
              }`}
            >
              {droppedAttachments
                ? "選んでいた発注書・図面は外しました。"
                : "このファイルでは発注書・図面を一緒に上げられません。"}
              （どの案件に付けるか決められないため）起票後に案件ごとに上げてください。
            </p>
          )}

          {checking && (
            <p className="flex items-center gap-2 text-sm text-default-600">
              <Spinner size="sm" />
              ファイルの様式を確認しています…
            </p>
          )}
          {orderError && <p className="text-sm text-danger">{orderError}</p>}
          {orderPreview && <OrderPreview preview={orderPreview} />}
          {shintoPreview && <ShintoPreview preview={shintoPreview} />}

          {progress && <p className="text-sm text-default-600">{progress}</p>}
          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="project-submit"
            isLoading={saving}
            // 読めない Excel を選んだまま押せると、何が起きるか分からないまま
            // 失敗する。確認中と、取り込めない紙のときは押させない。
            isDisabled={
              checking ||
              orderError != null ||
              orderPreview?.can_apply === false ||
              shintoPreview?.can_apply === false
            }
            onPress={() => void submit()}
          >
            {shintoPreview?.can_apply
              ? `${shintoPreview.project_count} 件の案件を登録`
              : orderPreview?.can_apply
                ? "この内容で登録"
                : "登録"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
